import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Loader2 } from "lucide-react";
import { api, uploadImage } from "@/admin/api";

const services = [
  "AI Strategy & Executive Advisory",
  "Custom AI Implementation",
  "Custom Development",
  "Digital Transformation",
  "High-ROI Marketing",
];

const empty = {
  title: "",
  client: "",
  industry: "",
  service: services[0],
  challenge: "",
  result: "",
  metric: "",
  image_path: null,
  published: true,
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const inputClass =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base text-[#0F172A] placeholder:text-slate-400 outline-none transition-all focus:border-[#0FA89E] focus:ring-4 focus:ring-[rgba(15,168,158,0.1)]";

export const CaseStudyManager = ({ studies, reload }) => {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const openNew = () => {
    setForm(empty);
    setEditing("new");
  };

  const openEdit = (s) => {
    setForm({
      title: s.title,
      client: s.client,
      industry: s.industry,
      service: s.service,
      challenge: s.challenge,
      result: s.result,
      metric: s.metric,
      image_path: s.image_path || null,
      published: s.published,
    });
    setEditing(s.id);
  };

  const [uploading, setUploading] = useState(false);

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = await uploadImage(file);
      setForm((f) => ({ ...f, image_path: data.path }));
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const close = () => setEditing(null);
  const update = (key) => (e) =>
    setForm({ ...form, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === "new") {
        await api("/api/admin/case-studies", { method: "POST", body: JSON.stringify(form) });
        toast.success("Case study added.");
      } else {
        await api(`/api/admin/case-studies/${editing}`, { method: "PUT", body: JSON.stringify(form) });
        toast.success("Case study updated.");
      }
      close();
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this case study?")) return;
    try {
      await api(`/api/admin/case-studies/${id}`, { method: "DELETE" });
      toast.success("Case study deleted.");
      reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div data-testid="admin-case-studies">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-xl font-semibold text-[#0F172A]">
          Case studies ({studies.length})
        </h2>
        <button
          data-testid="case-study-add-button"
          onClick={openNew}
          className="inline-flex items-center gap-2 rounded-full bg-[#0FA89E] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0C8E85] transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add case study
        </button>
      </div>

      <div className="space-y-4">
        {studies.map((s) => (
          <div
            key={s.id}
            data-testid={`case-study-row-${s.id}`}
            className="rounded-xl border border-slate-200 bg-white p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
          >
            <div className="min-w-0 flex items-start gap-4">
              {s.image_path && (
                <img
                  src={`${BACKEND_URL}/api/files/${s.image_path}`}
                  alt={s.title}
                  className="w-16 h-16 rounded-lg object-cover shrink-0 border border-slate-200"
                />
              )}
              <div className="min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <h3 className="font-semibold text-[#0F172A] truncate">{s.title}</h3>
                <span
                  className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    s.published
                      ? "bg-teal-50 text-teal-700 border border-teal-200"
                      : "bg-slate-100 text-slate-500 border border-slate-200"
                  }`}
                >
                  {s.published ? "Published" : "Hidden"}
                </span>
              </div>
              <p className="text-sm text-slate-500">
                {s.client} · {s.industry} · {s.service}
              </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                data-testid={`case-study-edit-${s.id}`}
                onClick={() => openEdit(s)}
                className="p-2.5 rounded-lg border border-slate-200 text-slate-600 hover:border-[#0FA89E] hover:text-[#0FA89E] transition-colors"
                aria-label="Edit"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                data-testid={`case-study-delete-${s.id}`}
                onClick={() => remove(s.id)}
                className="p-2.5 rounded-lg border border-slate-200 text-slate-600 hover:border-red-300 hover:text-red-600 transition-colors"
                aria-label="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        {studies.length === 0 && (
          <p className="text-slate-500 text-sm py-8 text-center">
            No case studies yet. Add your first one.
          </p>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B0F19]/50 backdrop-blur-sm px-4">
          <form
            onSubmit={save}
            data-testid="case-study-form"
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-7 sm:p-8 shadow-2xl"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-xl font-semibold text-[#0F172A]">
                {editing === "new" ? "New case study" : "Edit case study"}
              </h3>
              <button type="button" onClick={close} className="p-2 text-slate-400 hover:text-slate-700" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Title</label>
                <input required minLength={3} value={form.title} onChange={update("title")} data-testid="case-study-input-title" className={inputClass} placeholder="Warehouse operations, rebuilt around AI" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Client</label>
                <input required value={form.client} onChange={update("client")} data-testid="case-study-input-client" className={inputClass} placeholder="Regional logistics provider" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Industry</label>
                <input required value={form.industry} onChange={update("industry")} data-testid="case-study-input-industry" className={inputClass} placeholder="Logistics & Supply Chain" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Service</label>
                <select value={form.service} onChange={update("service")} data-testid="case-study-input-service" className={inputClass}>
                  {services.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Headline metric</label>
                <input required value={form.metric} onChange={update("metric")} data-testid="case-study-input-metric" className={inputClass} placeholder="38% faster fulfillment" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Challenge</label>
                <textarea required minLength={10} rows={3} value={form.challenge} onChange={update("challenge")} data-testid="case-study-input-challenge" className={`${inputClass} resize-none`} placeholder="What was broken..." />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Result</label>
                <textarea required minLength={10} rows={3} value={form.result} onChange={update("result")} data-testid="case-study-input-result" className={`${inputClass} resize-none`} placeholder="What we delivered..." />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Photo</label>
                <div className="flex items-center gap-4">
                  {form.image_path && (
                    <img
                      src={`${BACKEND_URL}/api/files/${form.image_path}`}
                      alt="Case study"
                      className="w-20 h-20 rounded-lg object-cover border border-slate-200"
                      data-testid="case-study-image-preview"
                    />
                  )}
                  <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 cursor-pointer hover:border-[#0FA89E] transition-colors">
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4" />
                        {form.image_path ? "Replace photo" : "Upload photo"}
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      onChange={pickImage}
                      data-testid="case-study-image-input"
                    />
                  </label>
                </div>
              </div>
              <label className="flex items-center gap-3 text-sm font-semibold text-slate-700">
                <input type="checkbox" checked={form.published} onChange={update("published")} data-testid="case-study-input-published" className="w-4 h-4 accent-[#0FA89E]" />
                Published on website
              </label>
            </div>
            <button
              type="submit"
              data-testid="case-study-save-button"
              disabled={saving}
              className="w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-[#0FA89E] px-8 py-3.5 text-base font-semibold text-white hover:bg-[#0C8E85] disabled:opacity-60"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save case study"
              )}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};
